import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography, useMediaQuery, useTheme, ToggleButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { alpha } from '@mui/material/styles';
import AvatarInfoCard from '@ui/components/common/AvatarInfoCard';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import CustomDialog from '@ui/components/common/Dialog';
import { UserTick, Export } from 'iconsax-react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { AllotteAccommodationToAttendeeAsync, downloadAccommodationExcelAsync, getAllAccommodationsAsync, getCarAccommodationEligibleAttendeesAsync, moveToCarAccommodationAsync } from '@application/features/familyFest/familyFestAccommodationThunk';
import { FamilyFestComingWithEnum, FamilyFestTravelModeEnum } from '@application/enums/family-fest-registration-enums';
import { AccommodationStatusEnum } from '@application/enums/accommodation-status-enum';
import FamilyInfoDialog from '../ResponsesTab/FamilyResponsesGrid/FamilyInfoDialog';
const AccommodationTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    // State
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [unallocateDialogOpen, setUnallocateDialogOpen] = useState(false);
    const [selectedUnallocateRowId, setSelectedUnallocateRowId] = useState(null);
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
    // Family Info Popup State
    const [familyInfoOpen, setFamilyInfoOpen] = useState(false);
    const [familyInfoPersons, setFamilyInfoPersons] = useState([]);
    // Move-to-accommodation dialog: own fetched eligible-car-traveler list + own selection.
    const [accommodationDialogOpen, setAccommodationDialogOpen] = useState(false);
    const [accommodationEligibleRows, setAccommodationEligibleRows] = useState([]);
    const [accommodationEligibleTotalCount, setAccommodationEligibleTotalCount] = useState(0);
    const [accommodationEligibleLoading, setAccommodationEligibleLoading] = useState(false);
    const [accommodationPaginationModel, setAccommodationPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [selectedAccommodationAttendeeIds, setSelectedAccommodationAttendeeIds] = useState([]);
    const [movingToAccommodation, setMovingToAccommodation] = useState(false);
    const [accommodationSearch, setAccommodationSearch] = useState('');
    const [debouncedAccommodationSearch, setDebouncedAccommodationSearch] = useState('');
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedAccommodationSearch(accommodationSearch.trim());
            setAccommodationPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }, 300);
        return () => clearTimeout(id);
    }, [accommodationSearch]);
    // Redux
    const currentUserRoles = useAppSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) &&
        (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const isHappinessManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('HappinessManager');
    const { accommodationDetails, selectedAccommodationAttendee } = useAppSelector((state) => state.familyFestAccommodation);
    const VdkEventForDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const companyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const fetchAccommodations = () => {
        const sort = sortModel[0];
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue.trim() || undefined,
            name: filters.attendeeName,
            companyId: filters.company,
            comingWith: filters.comingWith,
            travelMode: filters.travelMode,
            accommodationStatus: filters.accommodationStatus,
            adultCount: filters.adultCount,
            kidsCount: filters.kidsCount,
            babyBedCount: filters.babyBedCount,
            sortBy: sort?.field,
            sortOrder: sort?.sort ?? 'desc'
        };
        dispatch(getAllAccommodationsAsync(request));
    };
    const handleExportExcel = async () => {
        const eventId = VdkEventForDetails?.id;
        if (!eventId)
            return;
        setIsDownloadingExcel(true);
        try {
            await dispatch(downloadAccommodationExcelAsync(eventId)).unwrap();
        }
        finally {
            setIsDownloadingExcel(false);
        }
    };
    const eventId = VdkEventForDetails?.id;
    const fetchAccommodationEligibleAttendees = useCallback(() => {
        if (!eventId)
            return;
        setAccommodationEligibleLoading(true);
        dispatch(getCarAccommodationEligibleAttendeesAsync({
            eventId,
            request: {
                pageNumber: accommodationPaginationModel.page + 1,
                pageSize: accommodationPaginationModel.pageSize,
                searchString: debouncedAccommodationSearch || undefined
            }
        }))
            .unwrap()
            .then((data) => {
            setAccommodationEligibleRows(data.items);
            setAccommodationEligibleTotalCount(data.totalCount);
        })
            .catch(() => {
            toast.error(t('events.eventDetails.responsesTab.failedToLoadAccommodationEligible', 'Could not load eligible attendees'));
        })
            .finally(() => setAccommodationEligibleLoading(false));
    }, [dispatch, eventId, accommodationPaginationModel, debouncedAccommodationSearch, t]);
    useEffect(() => {
        if (!accommodationDialogOpen)
            return;
        fetchAccommodationEligibleAttendees();
    }, [accommodationDialogOpen, fetchAccommodationEligibleAttendees]);
    const handleOpenAccommodationDialog = () => {
        setSelectedAccommodationAttendeeIds([]);
        setAccommodationSearch('');
        setDebouncedAccommodationSearch('');
        setAccommodationPaginationModel({ page: 0, pageSize: 10 });
        setAccommodationDialogOpen(true);
    };
    const handleMoveToAccommodationConfirmed = () => {
        const attendeeIds = selectedAccommodationAttendeeIds.map(String);
        if (attendeeIds.length === 0)
            return;
        setMovingToAccommodation(true);
        dispatch(moveToCarAccommodationAsync({ attendeeIds }))
            .unwrap()
            .then(() => {
            toast.success(t('events.eventDetails.responsesTab.movedToAccommodationSuccessfully', '{{count}} attendee(s) moved to accommodation', { count: attendeeIds.length }));
            fetchAccommodations();
        })
            .catch((e) => {
            toast.error(e instanceof Error
                ? e.message
                : t('events.eventDetails.responsesTab.failedToMoveToAccommodation', 'Could not move attendee(s) to accommodation'));
        })
            .finally(() => {
            setMovingToAccommodation(false);
            setAccommodationDialogOpen(false);
            setSelectedAccommodationAttendeeIds([]);
        });
    };
    const accommodationPreviewColumns = useMemo(() => [
        {
            field: 'firstName',
            headerName: t('familyForm.firstName', 'First name') || 'First name',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                return (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, children: [_jsx("span", { children: params.value || '-' }), row.isAccommodationViaFamilyMember && (_jsx(Chip, { label: t('events.eventDetails.accommodationTab.accommodationViaFamilyMember'), size: 'small', color: 'warning', variant: 'soft', sx: { height: 20, fontSize: 11, width: 'fit-content' } }))] }));
            }
        },
        {
            field: 'lastName',
            headerName: t('familyForm.lastName', 'Last name') || 'Last name',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'companyName',
            headerName: t('familyForm.company', 'Company') || 'Company',
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'travelMode',
            headerName: t('familyForm.travelMode', 'Travel mode') || 'Travel mode',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                const n = String(params.value ?? '')
                    .trim()
                    .toLowerCase();
                if (n === 'auto' || n === 'car')
                    return t('familyFest.travelModeCar', 'Car');
                if (n === 'bus')
                    return t('familyFest.travelModeBus', 'Bus');
                if (n === 'airplane' || n === 'plane')
                    return t('familyFest.travelModePlane', 'Plane');
                return params.value || '-';
            }
        }
    ], [t]);
    useEffect(() => {
        fetchAccommodations();
    }, [
        dispatch,
        paginationModel.page,
        paginationModel.pageSize,
        sortModel,
        searchValue,
        filters,
        selectedAccommodationAttendee,
        companyIds
    ]);
    const rows = useMemo(() => accommodationDetails?.pagedResponse?.items ?? [], [accommodationDetails]);
    const selectedUnallocateRow = rows.find((row) => row.id === selectedUnallocateRowId) ?? null;
    const mapComingWith = (value) => {
        switch (value) {
            case FamilyFestComingWithEnum.Alone:
                return t('familyForm.alone');
            case FamilyFestComingWithEnum.Guest:
                return t('familyForm.comingWithGuest');
            case FamilyFestComingWithEnum.Family:
                return t('familyForm.comingWithFamily');
            default:
                return value;
        }
    };
    const getAccommodationStatusLabel = (status) => {
        switch (status) {
            case AccommodationStatusEnum.OptedFor:
                return t('events.eventDetails.accommodationTab.status.optedFor');
            case AccommodationStatusEnum.OptedOut:
                return t('events.eventDetails.accommodationTab.status.optedOut');
            case AccommodationStatusEnum.Unavailable:
                return t('events.eventDetails.accommodationTab.status.unavailable');
            default:
                return status;
        }
    };
    const handleToggleAllotment = (row) => {
        if (row.isAllotted) {
            setSelectedUnallocateRowId(row.id);
            setUnallocateDialogOpen(true);
            return;
        }
        dispatch(AllotteAccommodationToAttendeeAsync({
            attendeeId: row.id,
            toAllotte: true
        })).unwrap();
    };
    const handleOpenFamilyInfo = (row) => {
        // Accommodation is family-wide: once assigned (bus pickup or car), it covers the whole
        // family, so every person shows the same accommodation value. Matches the same pattern used
        // in ResponsesTab/FamilyResponsesGrid's openFamilyInfoForRow (detail.isAccommodationAssigned).
        const accommodationRequired = String(row.isAccommodationAssigned ?? false);
        // Primary attendee details (some fields may be empty in this view but required for index 0)
        const primary = {
            firstName: row.attendeeName,
            middleName: '',
            lastName: '',
            relationshipToYou: '',
            DateOfBirth: null,
            email: '',
            Address: '',
            TravelMode: '',
            Option: 'false',
            accommodationRequired
        };
        // Mapping members from the row
        const members = (row.familyFestAttendeeMembers ?? []).map((member) => ({
            firstName: member.firstName ?? '',
            middleName: member.middleName ?? '',
            lastName: member.lastName ?? '',
            relationshipToYou: member.relationshipToPrimary ?? '',
            DateOfBirth: member.dateOfBirth ?? null,
            email: member.email ?? '',
            Address: member.addressLine ?? '',
            TravelMode: member.travelMode ?? '',
            Option: String(member.isWheelchairDependent),
            accommodationRequired
        }));
        setFamilyInfoPersons([primary, ...members]);
        setFamilyInfoOpen(true);
    };
    const handleConfirmUnallocate = () => {
        if (selectedUnallocateRowId) {
            dispatch(AllotteAccommodationToAttendeeAsync({
                attendeeId: selectedUnallocateRowId,
                toAllotte: false
            })).unwrap();
        }
        setSelectedUnallocateRowId(null);
        setUnallocateDialogOpen(false);
    };
    const handleCloseUnallocateDialog = () => {
        setSelectedUnallocateRowId(null);
        setUnallocateDialogOpen(false);
    };
    const companyFilterOptions = useMemo(() => {
        return (VdkEventForDetails?.eventCompanies ?? []).map((company) => ({
            value: company.value,
            label: company.label
        }));
    }, [VdkEventForDetails?.eventCompanies]);
    const columns = [
        {
            field: 'attendeeName',
            headerName: t('events.eventDetails.transportTab.attendeeName'),
            flex: 0.8,
            filterable: true,
            type: 'string',
            minWidth: 180,
            renderCell: (params) => (_jsx(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', children: params.row.attendeeName }) }))
        },
        {
            field: 'company',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 0.8,
            type: 'string',
            filterable: true,
            minWidth: 180
        },
        {
            field: 'travelMode',
            headerName: t('familyForm.travelMode', 'Travel mode'),
            flex: 0.5,
            type: 'string',
            filterable: true,
            minWidth: 120,
            renderCell: (params) => {
                const raw = String(params.row.travelMode ?? '')
                    .trim()
                    .toLowerCase();
                const label = raw === 'auto'
                    ? t('familyFest.travelModeCar', 'Car')
                    : raw === 'bus'
                        ? t('familyFest.travelModeBus', 'Bus')
                        : raw === 'plane'
                            ? t('familyFest.travelModePlane', 'Plane')
                            : '-';
                return _jsx(Typography, { variant: 'body2', children: label });
            }
        },
        {
            field: 'comingWith',
            headerName: t('events.eventDetails.transportTab.comingWith'),
            flex: 0.4,
            filterable: true,
            type: 'string',
            minWidth: 150,
            renderCell: (params) => {
                const isGuest = ['guest', 'introduction'].includes(String(mapComingWith(params.row.comingWith) ?? '')
                    .trim()
                    .toLowerCase());
                return (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', children: mapComingWith(params.row.comingWith) }), Number(params.row.totalMembers) > 0 && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', sx: {
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }, onClick: (e) => {
                                e.stopPropagation();
                                handleOpenFamilyInfo(params.row);
                            }, children: `${params.row.totalMembers} ${isGuest ? t('events.eventDetails.transportTab.member') : t('events.eventDetails.transportTab.members')}` }))] }));
            }
        },
        {
            field: 'adultCount',
            headerName: t('events.eventDetails.accommodationTab.adultCount', 'Adults'),
            flex: 0.3,
            type: 'number',
            minWidth: 90
        },
        {
            field: 'kidsCount',
            headerName: t('events.eventDetails.accommodationTab.kidsCount', 'Kids'),
            flex: 0.3,
            type: 'number',
            minWidth: 90
        },
        {
            field: 'babyBedCount',
            headerName: t('events.eventDetails.accommodationTab.babyBedCount', 'Baby Beds'),
            flex: 0.3,
            type: 'number',
            minWidth: 100
        },
        // {
        //   field: 'accommodationStatus',
        //   headerName: t<string>('events.eventDetails.accommodationTab.accommodation'),
        //   flex: 0.8,
        //   filterable: true,
        //   type: 'string',
        //   minWidth: 150,
        //   renderCell: (params: GridRenderCellParams<FamilyFestAttendeeAccommodationRecord>) => {
        //     const status = params.value as AccommodationStatusEnum;
        //     const statusColor =
        //       status === AccommodationStatusEnum.OptedFor
        //         ? 'success'
        //         : status === AccommodationStatusEnum.OptedOut
        //           ? 'warning'
        //           : 'error';
        //     return (
        //       <Button variant='soft' color={statusColor} sx={{ color: `${statusColor}.dark` }}>
        //         {getAccommodationStatusLabel(status)}
        //       </Button>
        //     );
        //   }
        // },
        // {
        //   field: 'comments',
        //   headerName: t<string>('events.eventDetails.accommodationTab.details'),
        //   flex: 0.8,
        //   filterable: false,
        //   minWidth: 220,
        //   renderCell: (params: GridRenderCellParams<FamilyFestAttendeeAccommodationRecord>) => (
        //     <Tooltip title={params.value}>
        //       <Typography
        //         variant='body2'
        //         color='text.secondary'
        //         sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
        //       >
        //         {params.value || '-'}
        //       </Typography>
        //     </Tooltip>
        //   )
        // },
        {
            field: 'actions',
            headerName: t('events.eventDetails.accommodationTab.action'),
            flex: 0.8,
            sortable: false,
            filterable: false,
            minWidth: 170,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isOptedFor = params.row.accommodationStatus === AccommodationStatusEnum.OptedFor;
                const isAllotted = params.row.isAllotted;
                const buttonLabel = t('events.eventDetails.accommodationTab.markAsAllotted');
                const isDisabled = !isOptedFor;
                const selectedStyle = isOptedFor && isAllotted;
                return (_jsxs(ToggleButton, { value: params.row.id, selected: selectedStyle, size: 'small', disabled: isDisabled, sx: {
                        textTransform: 'none',
                        minWidth: 150,
                        gap: 1,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: selectedStyle
                            ? theme.palette.primary.main
                            : alpha(theme.palette.grey[500], 0.32),
                        backgroundColor: selectedStyle
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                        color: selectedStyle ? theme.palette.common.white : theme.palette.text.primary,
                        '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main
                        },
                        '&.Mui-disabled': {
                            borderColor: alpha(theme.palette.grey[500], 0.24),
                            color: alpha(theme.palette.text.primary, 0.4)
                        }
                    }, onClick: (e) => {
                        e.stopPropagation();
                        handleToggleAllotment(params.row);
                    }, children: [_jsx(Icon, { icon: 'ic:round-check', width: 20, height: 20, color: isOptedFor && !isAllotted ? theme.palette.action.disabled : undefined }), buttonLabel] }));
            }
        }
    ];
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.accommodationTab.optedForAccommodation'), labelIcon: _jsx(UserTick, { variant: 'Outline', size: '20px' }), subheader: String(accommodationDetails?.totalOptedFor ?? 0) }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.accommodationTab.totalAdults', 'Total Adults'), labelIcon: _jsx(Icon, { icon: 'mdi:account-group', width: 20, height: 20 }), subheader: String(accommodationDetails?.totalAdults ?? 0) }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.accommodationTab.totalKids', 'Total Kids'), labelIcon: _jsx(Icon, { icon: 'mdi:baby-face-outline', width: 20, height: 20 }), subheader: String(accommodationDetails?.totalKids ?? 0) }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.accommodationTab.totalBabyBeds', 'Total Beds'), labelIcon: _jsx(Icon, { icon: 'hugeicons:bed-double', width: 20, height: 20 }), subheader: String(accommodationDetails?.totalBabyBeds ?? 0) }) })] }), _jsx(PrimaryGrid, { columns: columns, rows: rows, showHeaderControls: false, showNewHeaderControls: true, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, filterOptions: {
                    comingWith: [
                        { value: FamilyFestComingWithEnum.Alone, label: t('familyForm.alone') },
                        { value: FamilyFestComingWithEnum.Guest, label: t('familyForm.comingWithGuest') },
                        { value: FamilyFestComingWithEnum.Family, label: t('familyForm.comingWithFamily') }
                    ],
                    travelMode: [
                        { value: FamilyFestTravelModeEnum.Bus, label: t('familyFest.travelModeBus', 'Bus') },
                        { value: FamilyFestTravelModeEnum.Auto, label: t('familyFest.travelModeCar', 'Car') },
                        {
                            value: FamilyFestTravelModeEnum.Plane,
                            label: t('familyFest.travelModePlane', 'Plane')
                        }
                    ],
                    // accommodationStatus: [
                    //   {
                    //     value: AccommodationStatusEnum.OptedFor,
                    //     label: getAccommodationStatusLabel(AccommodationStatusEnum.OptedFor)
                    //   },
                    //   {
                    //     value: AccommodationStatusEnum.OptedOut,
                    //     label: getAccommodationStatusLabel(AccommodationStatusEnum.OptedOut)
                    //   }
                    // ],
                    company: companyFilterOptions
                }, resetFiltersKey: resetFiltersKey, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', rowCount: accommodationDetails?.pagedResponse?.totalCount ?? 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? 1.5 : 0, children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.accommodationTab.accommodationDetails') }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [!isCollaborator && isHappinessManager && (_jsx(Button, { startIcon: _jsx(Icon, { icon: 'mdi:car-arrow-right', width: 20, height: 20 }), size: 'large', variant: 'outlined', color: 'primary', onClick: handleOpenAccommodationDialog, children: t('events.eventDetails.responsesTab.addToAccommodation', 'Add to Accommodation') })), _jsx(Button, { startIcon: _jsx(Export, { variant: 'Outline' }), size: 'large', variant: 'outlined', onClick: handleExportExcel, disabled: isDownloadingExcel, color: 'primary', children: t('common.exportToExcel', 'Export to Excel') }), _jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(SearchFilter, { forceDesktopVariant: true, widthBox: '150px', searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('placeholders.search'), onClear: () => {
                                            setSearchValue('');
                                            setFilters({});
                                            setResetFiltersKey((prev) => prev + 1);
                                        }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                            setFilters({});
                                            setResetFiltersKey((k) => k + 1);
                                        }, isDatagrid: true, iconButtonSize: 'medium', onFilterClick: () => setGridFilterOpen(true), filterLabel: t('users.filters') }) })] })] }), filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, maxHeight: 'calc(100dvh - 350px)', boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(ConfirmationDialog, { open: unallocateDialogOpen, onClose: handleCloseUnallocateDialog, maxWidth: 'sm', title: t('events.eventDetails.accommodationTab.confirmationTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: handleCloseUnallocateDialog, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'error', onClick: handleConfirmUnallocate, children: t('actions.unallocateNow') })] }), children: _jsx(Box, { px: 2.5, py: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.accommodationTab.confirmUnallocate', {
                            attendee: selectedUnallocateRow?.attendeeName ?? ''
                        }) }) }) }), _jsxs(CustomDialog, { open: accommodationDialogOpen, onClose: () => {
                    if (movingToAccommodation)
                        return;
                    setAccommodationDialogOpen(false);
                }, titleWithIcon: t('events.eventDetails.responsesTab.addToAccommodationTitle', 'Add to Accommodation'), titleIcon: '/assets/icons/star_header.png', maxWidth: 'lg', backgroundVariant: 'white', closeIcon: true, actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', disabled: movingToAccommodation, onClick: () => setAccommodationDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, size: 'large', variant: 'contained', color: 'primary', disabled: movingToAccommodation || selectedAccommodationAttendeeIds.length === 0, onClick: handleMoveToAccommodationConfirmed, children: movingToAccommodation ? (_jsx(CircularProgress, { size: 20, color: 'inherit' })) : (t('events.eventDetails.responsesTab.addToAccommodation', 'Add to Accommodation')) })] }), children: [_jsx(Box, { sx: { px: 3, pb: 1 }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', textAlign: 'center', children: t('events.eventDetails.responsesTab.addToAccommodationSubtitle', {
                                count: selectedAccommodationAttendeeIds.length,
                                defaultValue: '{{count}} car traveler(s) selected. Their whole family will be moved into accommodation.'
                            }) }) }), _jsx(Box, { sx: {
                            px: 3,
                            pb: 1.5,
                            '& .MuiTextField-root': { bgcolor: 'transparent' },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: theme.palette.common.grey8,
                                borderRadius: '16px'
                            }
                        }, children: _jsx(SearchFilter, { fullWidth: true, forceDesktopVariant: true, searchValue: accommodationSearch, onSearchChange: setAccommodationSearch, placeholder: t('events.eventDetails.transportTab.searchAttendees'), breakpoint: 'lg', onClear: () => setAccommodationSearch(''), widthBox: '100%', bgVariant: 'grey8' }) }), _jsx(Box, { sx: { px: 3, pb: 3 }, children: _jsx(PrimaryGrid, { loading: accommodationEligibleLoading, columns: accommodationPreviewColumns, rows: accommodationEligibleRows, getRowId: (row) => row.id, showHeaderControls: false, showNewHeaderControls: false, maxHeight: '300px', noRowsText: t('events.eventDetails.responsesTab.noResponses'), isMultiple: true, disableRowSelectionOnClick: true, rowSelectionModel: selectedAccommodationAttendeeIds, onSelectionChange: setSelectedAccommodationAttendeeIds, paginationMode: 'server', paginationModel: accommodationPaginationModel, onPaginationModelChange: setAccommodationPaginationModel, rowCount: accommodationEligibleTotalCount, pageSizeOptions: [5, 10, 25, 50] }) })] }), _jsx(FamilyInfoDialog, { open: familyInfoOpen, onClose: () => setFamilyInfoOpen(false), persons: familyInfoPersons })] }));
};
export default AccommodationTab;
